import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { mmss, elapsedSecs } from "../lib/format";
import { playChime } from "../lib/chime";
import { cn } from "../components/ui/kit";

export default function KitchenApp() {
  const { data: cfg } = usePoll(() => api.get("/config").then((r) => r.data), 0);
  const { data: tickets, refetch } = usePoll(() => api.get("/kitchen/tickets").then((r) => r.data), 2500);
  const [now, setNow] = useState(Date.now());
  const chimed = useRef(new Set());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // chime on new tickets
  useEffect(() => {
    if (!tickets) return;
    const autoChime = cfg?.feature_toggles?.auto_chime !== false;
    tickets.forEach((t) => {
      if (t.chime && !chimed.current.has(t.id)) {
        chimed.current.add(t.id);
        if (autoChime) playChime();
        api.post(`/kitchen/tickets/${t.id}/ack-chime`).catch(() => {});
      }
    });
  }, [tickets, cfg]);

  const greenMax = (cfg?.kitchen_timer?.green_max_min ?? 10) * 60;
  const yellowMax = (cfg?.kitchen_timer?.yellow_max_min ?? 30) * 60;

  const setStatus = async (t, status) => {
    try { await api.post(`/kitchen/tickets/${t.id}/status`, { status }); refetch(); }
    catch (e) { toast.error("Update failed"); }
  };

  const active = (tickets || []).filter((t) => t.status !== "served");
  // all-day aggregate
  const allday = {};
  active.forEach((t) => t.items.forEach((it) => { allday[it.name] = (allday[it.name] || 0) + it.qty; }));

  return (
    <div className="min-h-[calc(100vh-49px)] bg-black text-white">
      {/* all-day bar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800 bg-zinc-950 px-4 py-2">
        <span className="font-head text-sm font-black uppercase tracking-widest text-zinc-500">All-Day</span>
        {Object.keys(allday).length === 0 && <span className="text-sm text-zinc-600">Kitchen clear</span>}
        {Object.entries(allday).map(([n, q]) => (
          <span key={n} className="rounded-lg bg-zinc-800 px-3 py-1 text-sm font-bold"><span className="text-emerald-400">{q}×</span> {n}</span>
        ))}
      </div>

      <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {active.length === 0 && <p className="text-zinc-600">No active tickets. Confirm an order from the Waiter role.</p>}
        {active.map((t) => {
          const secs = t.status === "ready" ? (t.prep_seconds || 0) : elapsedSecs(t.received_at);
          const color = secs < greenMax ? "green" : secs < yellowMax ? "yellow" : "red";
          const ring = { green: "border-emerald-500", yellow: "border-amber-500", red: "border-rose-500 rc-pulse" }[color];
          const txt = { green: "text-emerald-400", yellow: "text-amber-400", red: "text-rose-400" }[color];
          return (
            <div key={t.id} data-testid={`kds-ticket-${t.id}`} className={cn("flex flex-col rounded-2xl border-2 bg-zinc-950 p-4", t.status === "ready" ? "border-emerald-600 opacity-80" : ring)}>
              <div className="flex items-center justify-between">
                <div className="font-head text-2xl font-black">{t.channel === "DINE-IN" ? t.table : t.channel}</div>
                <div className="font-mono text-xs text-zinc-500">{t.order_number}</div>
              </div>
              <div className={cn("my-2 font-mono text-4xl font-black tabular", txt)} data-testid={`kds-timer-${t.id}`}>{mmss(secs)}</div>
              <div className="flex-1 space-y-2">
                {t.items.map((it, i) => (
                  <div key={i} className="rounded-lg bg-zinc-900 p-2">
                    <div className="text-lg font-bold"><span className="text-emerald-400">{it.qty}×</span> {it.name}</div>
                    {it.modifiers?.length > 0 && <div className="text-sm text-amber-300">↳ {it.modifiers.map((m) => m.name).join(", ")}</div>}
                    {it.special_instructions && <div className="text-sm italic text-rose-300">! {it.special_instructions}</div>}
                  </div>
                ))}
              </div>
              <div className="mt-3">
                {t.status === "new" && <Big onClick={() => setStatus(t, "preparing")} testid={`kds-start-${t.id}`} className="bg-indigo-600 hover:bg-indigo-500">Start Preparing</Big>}
                {t.status === "preparing" && <Big onClick={() => setStatus(t, "ready")} testid={`kds-ready-${t.id}`} className="bg-emerald-600 hover:bg-emerald-500">✓ Ready / Complete</Big>}
                {t.status === "ready" && <div className="rounded-xl bg-emerald-600/20 py-3 text-center font-bold text-emerald-300">READY · awaiting server</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Big({ children, onClick, className, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={cn("w-full rounded-xl py-3 text-base font-black text-white transition active:scale-95", className)}>{children}</button>
  );
}
